/**
 * Hook to reverse-geocode lat/lng into a human-readable address + city.
 *
 * Wraps the `reverseGeocode` service in React Query so identical coordinates
 * across many rows resolve with a single request and are cached indefinitely
 * (a coordinate's address does not change).
 */

import { useQuery } from '@tanstack/react-query'
import { reverseGeocode, roundCoord } from '@/services/reverseGeocode'

export interface ReverseGeocodeResult {
  address: string | null
  city: string | null
  isLoading: boolean
  error: Error | null
}

function isFiniteCoord(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

export function useReverseGeocode(
  lat?: number | null,
  lng?: number | null
): ReverseGeocodeResult {
  const enabled = isFiniteCoord(lat) && isFiniteCoord(lng)
  // Normalize so near-identical points share one cached query.
  const keyLat = enabled ? roundCoord(lat) : lat
  const keyLng = enabled ? roundCoord(lng) : lng

  const query = useQuery({
    queryKey: ['reverse-geocode', keyLat, keyLng],
    queryFn: () => reverseGeocode(lat as number, lng as number),
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  })

  return {
    address: query.data?.address ?? null,
    city: query.data?.city ?? null,
    isLoading: enabled && query.isLoading,
    error: (query.error as Error | null) ?? null,
  }
}
